import UsersService from '@/services/UsersService'

const state = () => ({
  rows: [],
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  },
  loadingList: false,
  loadingOne: false,
  saving: false,
  deleting: false,
  error: null,
  current: null
})

const getters = {
  rows: (s) => s.rows,
  meta: (s) => s.meta,
  loadingList: (s) => s.loadingList,
  loadingOne: (s) => s.loadingOne,
  saving: (s) => s.saving,
  deleting: (s) => s.deleting,
  error: (s) => s.error,
  current: (s) => s.current
}

const mutations = {
  SET_LOADING_LIST(s, v) { s.loadingList = v },
  SET_LOADING_ONE(s, v) { s.loadingOne = v },
  SET_SAVING(s, v) { s.saving = v },
  SET_DELETING(s, v) { s.deleting = v },
  SET_ERROR(s, v) { s.error = v },

  SET_LIST(s, paginator) {
    const p = paginator || {}
    s.rows = Array.isArray(p.data) ? p.data : []
    s.meta = {
      current_page: Number(p.current_page || 1),
      last_page: Number(p.last_page || 1),
      per_page: Number(p.per_page || 10),
      total: Number(p.total || 0)
    }
  },

  SET_CURRENT(s, user) {
    s.current = user || null
  },

  UPSERT_IN_LIST(s, user) {
    if (!user?.id) return
    const idx = s.rows.findIndex(r => r.id === user.id)
    if (idx === -1) s.rows = [user, ...s.rows]
    else s.rows.splice(idx, 1, user)
  },

  REMOVE_FROM_LIST(s, id) {
    s.rows = s.rows.filter(r => r.id !== id)
  }
}

const actions = {
  async fetchList({ commit }, params = {}) {
    commit('SET_LOADING_LIST', true)
    commit('SET_ERROR', null)
    try {
      const res = await UsersService.list(params)
      const paginator = res?.data ?? res
      commit('SET_LIST', paginator)
      return paginator
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load users')
      throw e
    } finally {
      commit('SET_LOADING_LIST', false)
    }
  },

  async fetchOne({ commit }, { id, params } = {}) {
    if (!id) throw new Error('Missing id')
    commit('SET_LOADING_ONE', true)
    commit('SET_ERROR', null)
    try {
      const res = await UsersService.get(id, params || {})
      const payload = res?.data ?? res
      commit('SET_CURRENT', payload?.user ?? payload)
      return payload
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load user')
      throw e
    } finally {
      commit('SET_LOADING_ONE', false)
    }
  },

  async create({ commit }, payload) {
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await UsersService.create(payload)
      const user = res?.data ?? res
      commit('UPSERT_IN_LIST', user)
      commit('SET_CURRENT', user)
      return user
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to create user')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async update({ commit }, { id, payload }) {
    if (!id) throw new Error('Missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await UsersService.update(id, payload)
      const user = res?.data ?? res
      commit('UPSERT_IN_LIST', user)
      commit('SET_CURRENT', user)
      return user
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to update user')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async remove({ commit }, id) {
    if (!id) throw new Error('Missing id')
    commit('SET_DELETING', true)
    commit('SET_ERROR', null)
    try {
      await UsersService.destroy(id)
      commit('REMOVE_FROM_LIST', id)
      return true
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to delete user')
      throw e
    } finally {
      commit('SET_DELETING', false)
    }
  },

  async sendReset({ commit }, id) {
    if (!id) throw new Error('Missing id')
    commit('SET_ERROR', null)
    try {
      const res = await UsersService.sendResetLink(id)
      return res?.data ?? res
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to send reset link')
      throw e
    }
  },

  async bulk({ commit }, { action, ids, payload } = {}) {
    if (!action) throw new Error('Missing bulk action')
    if (!Array.isArray(ids) || !ids.length) throw new Error('Missing ids')
    commit('SET_ERROR', null)
    try {
      const res = await UsersService.bulk(action, ids, payload || {})
      return res?.data ?? res
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Bulk action failed')
      throw e
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
